import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Action, createSelector, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { filterBoolean } from '@recruitee/common';
import { DevFlag, DevFlagSelectors, injectDevFlag } from '@recruitee/dev-flags-data';
import { OfferItem, TalentPool, TalentPoolBrief } from '@recruitee/http-types';
import { memoizeSelector } from '@recruitee/ngrx';
import {
  TalentPoolItemsActions,
  TalentPoolItemsSelectors,
  TalentPoolsBriefActions,
  TalentPoolsBriefSelectors,
  TalentPoolsFullActions,
  TalentPoolsFullSelectors,
} from '@recruitee/offers-data';

import {
  TalentPoolsNewBriefActions,
  TalentPoolsNewBriefSelectors,
} from './talent-pool/talent-pool-brief.store';
import { TalentPoolsNewActions, TalentPoolsNewSelectors } from './talent-pool/talent-pool.store';
import { TalentPoolsScope } from './talent-pool/talent-pool.types';

const featureFlag = DevFlagSelectors.isEnabled(DevFlag.NewTalentPools);
const toNewTypeMap = (oldEntity: OfferItem) =>
  <TalentPool>{
    ...oldEntity,
    department: oldEntity?.department ?? null,
    example: oldEntity?.example ?? false,
  };

const getScope = (archived = false) =>
  archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default;

const collectionScoped = memoizeSelector((archived: boolean = false) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    TalentPoolsNewSelectors.entities(archived),
    (flag, oldEntities, newEntities) =>
      (flag ? newEntities : oldEntities.map(toNewTypeMap)).sort((a, b) => b.position - a.position),
  ),
);
const collectionBriefScoped = memoizeSelector((archived: boolean) =>
  createSelector(
    featureFlag,
    TalentPoolsBriefSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    TalentPoolsNewBriefSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    (flag, oldEntities, newEntities) =>
      (flag ? newEntities : oldEntities.map(entities => <TalentPoolBrief>entities)).sort(
        (a, b) => b.position - a.position,
      ),
  ),
);
const metaScoped = memoizeSelector((archived = false) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.meta({ scope: getScope(archived) }),
    TalentPoolsNewSelectors.meta({ scope: getScope(archived) }),
    (flag, oldMeta, newMeta) => (flag ? newMeta : oldMeta),
  ),
);

const entityMeta = memoizeSelector((idOrSlug: number | string) =>
  createSelector(
    featureFlag,
    TalentPoolsFullSelectors.metaByIdOrSlug(idOrSlug),
    TalentPoolsNewSelectors.metaByIdOrSlug(idOrSlug),
    (flag, oldMeta, newMeta) => (flag ? newMeta : oldMeta),
  ),
);

const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.byIdOrSlug(identifier),
    TalentPoolsNewSelectors.byIdOrSlug(identifier),
    (flag, oldEntity, newEntity) =>
      flag ? newEntity : oldEntity ? toNewTypeMap(oldEntity) : undefined,
  ),
);

const fullByIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(
    featureFlag,
    TalentPoolsFullSelectors.byIdOrSlug(identifier),
    TalentPoolsNewSelectors.byIdOrSlug(identifier),
    (flag, oldEntity, newEntity) =>
      flag ? newEntity : oldEntity ? toNewTypeMap(oldEntity) : undefined,
  ),
);

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsDataService {
  private hasNewTalentPoolsFeature = injectDevFlag(DevFlag.NewTalentPools);
  private store = inject(Store);
  private actions$ = inject(Actions);

  public collection$(archived: boolean = false): Observable<TalentPool[]> {
    return this.store.select(collectionScoped(archived)).pipe(filterBoolean);
  }

  public collectionBrief$(archived: boolean = false): Observable<TalentPoolBrief[]> {
    return this.store.select(collectionBriefScoped(archived)).pipe(filterBoolean);
  }

  public meta$(archived = false) {
    return this.store.select(metaScoped(archived));
  }

  public entityMeta$(idOrSlug: number | string) {
    return this.store.select(entityMeta(idOrSlug));
  }

  public entityByIdOrSlug$(idOrSlug: number | string): Observable<TalentPool | undefined> {
    return this.store.select(byIdOrSlug(idOrSlug));
  }

  public fullEntityByIdOrSlug$(idOrSlug: number | string): Observable<TalentPool | undefined> {
    return this.store.select(fullByIdOrSlug(idOrSlug));
  }

  public fetchCollection(archived: boolean = false): void {
    let action: Action;
    if (this.hasNewTalentPoolsFeature) {
      const scope: TalentPoolsScope = archived
        ? TalentPoolsScope.Archived
        : TalentPoolsScope.Default;
      action = TalentPoolsNewActions.fetchCollection({ payload: { scope } });
    } else {
      action = archived
        ? TalentPoolItemsActions.fetchArchivedCollection()
        : TalentPoolItemsActions.fetchActiveCollection();
    }
    this.store.dispatch(action);
  }

  public fetchBriefCollection(): void {
    const action = this.hasNewTalentPoolsFeature
      ? TalentPoolsNewBriefActions.fetchCollection()
      : TalentPoolsBriefActions.fetchCollection();
    this.store.dispatch(action);
  }

  public fetchOne(idOrSlug: number | string): void {
    const payload = { id: idOrSlug };
    const action = this.hasNewTalentPoolsFeature
      ? TalentPoolsNewActions.fetchOne({ payload })
      : TalentPoolsFullActions.fetchOne({
          payload,
        });
    this.store.dispatch(action);
  }

  public getFetchOneSuccessAction(): Actions<{
    payload: { id: number; offer?: { id: number }; talentPool?: { id: number } };
  }> {
    const action = this.hasNewTalentPoolsFeature
      ? TalentPoolsNewActions.fetchOneSuccess
      : TalentPoolsFullActions.fetchOneSuccess;
    return this.actions$.pipe(ofType(action));
  }

  public getFetchOneFailedAction(): Actions<{
    payload: { id: number; offer?: { id: number }; talentPool?: { id: number } };
  }> {
    const action = this.hasNewTalentPoolsFeature
      ? TalentPoolsNewActions.fetchOneFailed
      : TalentPoolsFullActions.fetchOneFailed;
    return this.actions$.pipe(ofType(action));
  }
}
